import { Line } from '@react-three/drei';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';
import { ISelectedSimpleMeasurement } from '../../@types/ContextProviderRelated';
import { EDraggablePointType, EInteractionMode, EUnitType } from '../../@types/Enums';
import useMeasurementDragging from '../../hooks/useMeasurementDragging';
import { useLanguage } from '../../provider/LanguageProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { INTERNAL_TO_CM_FACTOR } from '../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../utils/conversionUtils';
import { localizeNumber } from '../../utils/numberUtils';
import DraggablePoint from './DraggablePoint';
import RoundedBoxWithText from './RoundedBoxWithText';

interface SimpleMeasurementProps {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3 | null;
  selectedSimpleMeasurement: ISelectedSimpleMeasurement;
  measurementColor?: string;
  onClick: () => void;
  updateMeasurementPosition?: (newStartPoint: THREE.Vector3, newEndPoint: THREE.Vector3) => void;
  isTemporary?: boolean;
  raycaster: THREE.Raycaster;
  camera: THREE.Camera;
  scene: THREE.Scene;
}

const perpendicularLineLength = 0.8;

const SimpleMeasurement: React.FC<SimpleMeasurementProps> = ({
  startPoint,
  endPoint,
  selectedSimpleMeasurement,
  measurementColor = '#f89306',
  onClick,
  updateMeasurementPosition,
  isTemporary = false,
  raycaster,
  camera,
  scene,
}) => {
  const [startPointState, setStartPointState] = useState<THREE.Vector3>(startPoint);
  const [endPointState, setEndPointState] = useState<THREE.Vector3 | null>(endPoint);
  const [isHovered, setIsHovered] = useState(false);
  const { interactionMode } = useToolbarContext();
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();

  const lighterMeasurementColor = useMemo(() => lightenColor(measurementColor, 30), [measurementColor]);

  const color = useMemo(() => {
    if (isTemporary) return '#00ff00';
    return selectedSimpleMeasurement.simpleMeasurementIndex !== undefined
      ? '#009fe3'
      : isHovered
        ? lighterMeasurementColor
        : measurementColor;
  }, [selectedSimpleMeasurement, isHovered, isTemporary, measurementColor, lighterMeasurementColor]);

  const length = useMemo(() => {
    if (!endPointState) return null;
    const distanceInMeters = startPointState.distanceTo(endPointState) / INTERNAL_TO_CM_FACTOR;
    return distanceInMeters;
  }, [startPointState, endPointState]);

  const lengthText = useMemo(() => {
    if (!length) return '';
    return unitType === EUnitType.METRIC
      ? localizeNumber(length.toFixed(2), locale) + getLocalizedUnitLabel(intl, 'meter')
      : localizeNumber(
          convertTo(length, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2),
          locale,
        ) + getLocalizedUnitLabel(intl, 'foot');
  }, [length, unitType, locale, intl]);

  // Measurement dragging hook
  const { setDraggingPointType } = useMeasurementDragging(
    raycaster,
    camera,
    scene,
    (point) => {
      setStartPointState(point);
      if (updateMeasurementPosition && endPointState) {
        updateMeasurementPosition(point, endPointState);
      }
    },
    (point) => {
      setEndPointState(point);
      if (updateMeasurementPosition && startPointState) {
        updateMeasurementPosition(startPointState, point);
      }
    },
  );

  const handleStartDrag = (pointType: EDraggablePointType) => {
    setDraggingPointType(pointType);
  };

  useEffect(() => {
    setStartPointState(startPoint);
    setEndPointState(endPoint);
  }, [startPoint, endPoint]);

  if (!startPointState || !endPointState) {
    return null;
  }

  const lineWidth = isHovered || selectedSimpleMeasurement.simpleMeasurementIndex ? 4 : 3;

  // Compute perpendicular lines
  const direction = new THREE.Vector3().subVectors(endPointState, startPointState).normalize();
  const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0).normalize();

  const startPerpStart = new THREE.Vector3().addVectors(startPointState, perpendicular.clone().multiplyScalar(perpendicularLineLength / 2));
  const startPerpEnd = new THREE.Vector3().addVectors(startPointState, perpendicular.clone().multiplyScalar(-perpendicularLineLength / 2));

  const endPerpStart = new THREE.Vector3().addVectors(endPointState, perpendicular.clone().multiplyScalar(perpendicularLineLength / 2));
  const endPerpEnd = new THREE.Vector3().addVectors(endPointState, perpendicular.clone().multiplyScalar(-perpendicularLineLength / 2));

  return (
    <group
      onClick={(event) => {
        event.stopPropagation();
        if (interactionMode !== EInteractionMode.MOVE && onClick) {
          onClick();
        }
      }}
      renderOrder={9999}
      onPointerOver={() => setIsHovered(true)}
      onPointerOut={() => setIsHovered(false)}
      position={[0, 0, 5]}
    >
      {/* Dashed measurement line */}
      <Line points={[startPointState, endPointState]} color={color} lineWidth={lineWidth} dashed={true} dashOffset={2.5} dashScale={4} />

      {/* Perpendicular lines at both ends */}
      <Line points={[startPerpStart, startPerpEnd]} color={color} lineWidth={lineWidth} />
      <Line points={[endPerpStart, endPerpEnd]} color={color} lineWidth={lineWidth} />

      {/* Display the length */}
      {length && (
        <RoundedBoxWithText
          position={
            new THREE.Vector3(
              (startPointState.x + endPointState.x) / 2,
              (startPointState.y + endPointState.y) / 2,
              (startPointState.z + endPointState.z) / 2 + 0.5,
            )
          }
          fontSize={0.8}
          textColor="black"
          text={lengthText}
        />
      )}

      {/* Draggable points */}
      {!isTemporary && (
        <>
          <DraggablePoint
            position={startPointState}
            pointType={EDraggablePointType.START}
            onStartDragging={handleStartDrag}
            allowPropagation={false}
          />
          <DraggablePoint
            position={endPointState}
            pointType={EDraggablePointType.END}
            onStartDragging={handleStartDrag}
            allowPropagation={false}
          />
        </>
      )}
    </group>
  );
};

/**
 * Lightens a given hex color.
 */
function lightenColor(color: string, percent: number = 20): string {
  // Ensure the color starts with #
  if (color.startsWith('#')) {
    color = color.slice(1);
  }

  // Parse the RGB components
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);

  // Calculate the new RGB values, ensuring they don't exceed 255
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  // Convert the values back to hex and pad with leading zeros if necessary
  const newColor = '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');

  return newColor;
}

export default SimpleMeasurement;
